/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { ccReelEvent, ReelEvent } from '../../Reel/ReelEvent';
import ChildComponent from '../../Utility/Component/ChildComponent';
import ISymbolData from '../ISymbolData';
import { StreakEvent } from '../StreakEvent';
import StreakTrackerComponent from '../StreakTrackerComponent';
import ActivePaylineFilter from './Filters/ActivePaylineFilter';
import StreakTrackerFilterBase from './Filters/StreakTrackerFilterBase';
import ReelConfigComponent from './ReelConfigComponent';
import SymbolWorthComponent from './SymbolWorthComponent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Base clas for [[StreakTrackerComponent]] rules. */
@cc._decorator.ccclass
export default abstract class StreakTrackerRuleBase extends ChildComponent<StreakTrackerComponent>
{
    /** [[Symbol]] ID for which the rule applies. */
    @property({ visible: true })
    protected symbolId: string = "A";
    /** Public getter for ID. */
    public get SymbolId(): string { return this.symbolId; }

    /** Minimum symbols required for rule. */
    @property({ visible: true })
    protected minSymbolCount: number = 1;

    /** Maximum symbols allowed for rule. */
    @property({ visible: true })
    protected maxSymbolCount: number = 0;
    public get MaxSymbolCount(): number { return this.maxSymbolCount; }

    /** Minimum number of symbols needed to win. */
    @property({ visible: true })
    protected minSymbolsToWin: number = 1;
    public set MinSymbolsToWin(value: number) { this.minSymbolsToWin = value; }
    public get MinSymbolsToWin(): number { return this.minSymbolsToWin; }

    /** Minimum number of reels needed to streak. */
    @property({ visible: true })
    protected minReelCount: number = 1;

    /** Minimum number of reels neeeded to win. */
    @property({ visible: true })
    protected minReelsToWin: number = 1;

    /** Event name to fire this streak. */
    @property({ visible: true })
    public OnStreakEvent: string = StreakEvent.OnStreak;
    /** [[ReelEvent]] on which to send streak tracker events. */
    @property({ type: ccReelEvent })
    protected streakOnReelEvent: ReelEvent = ReelEvent.OnBounceStart;
    public get StreakOnReelEvent(): ReelEvent { return this.streakOnReelEvent; }

    /** Event name for clearing this streak. */
    @property({ visible: true })
    public OnClearEvent: string = StreakEvent.OnClear;
    @property()
    protected shouldClear: boolean = false;
    public get ShouldClear(): boolean { return this.shouldClear; }
    /** [[ReelEvent]] on which to clear streak tracker events. */
    @property({ type: ccReelEvent })
    protected clearOnReelEvent: ReelEvent = ReelEvent.OnStarted;
    public get ClearOnReelEvent(): ReelEvent { return this.clearOnReelEvent; }

    @property([cc.Integer])
    protected streakReels: number[] = [];
    private _isReelSpecific: boolean = false;

    /** Reel configurations. */
    protected reelConfigs: ReelConfigComponent[] = null;
    /** Symbol values. */
    protected symbolWorths: SymbolWorthComponent[] = null;
    /** Symbol ID to value map. */
    protected symbolWorthMap: Map<string, number> = new Map<string, number>();
    /** Rule filters. */
    protected filters: StreakTrackerFilterBase[] = null;

    /** Collection of [[SymbolData]] for filtering. */
    protected symbols: ISymbolData[] = [];
    /** Symbols within the scope of rule and filters. */
    protected validSymbols: ISymbolData[] = [];
    /** Symbols which have not been finalized. */
    protected remainingSymbols: number = 0;

    /** Symbols which have been sent streak event. */
    protected dispatchedSymbolNodes: cc.Node[] = [];

    protected hasStreaked: boolean = false;
    public get HasStreaked(): boolean { return this.hasStreaked; }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.loadChildren();
        if (this.streakReels.length > 0) { this._isReelSpecific = true; }
    }

    /** Load children and set parent to this. */
    private loadChildren(): void
    {
        this.reelConfigs = this.getComponentsInChildren(ReelConfigComponent).filter(x => x.enabled);
        this.reelConfigs.forEach(x => x.Parent = this);

        this.symbolWorths = this.getComponentsInChildren(SymbolWorthComponent).filter(x => x.enabled);
        this.symbolWorths.forEach(x =>
            {
                this.symbolWorthMap.set(x.SymbolId, x.Worth);
                x.Parent = this;
            }, this);

        this.filters = this.getComponentsInChildren(StreakTrackerFilterBase).filter(x => x.enabled);
        this.filters.forEach(x => x.Parent = this);
    }

    /**
     * Executles rule and sends events to unfiltered symbols.
     * @param symbols List of symbol information to filter upon.
     * @param remainingSymbols Number of symbols not landed.
     */
    public ExecuteRule = (symbols: ISymbolData[], remainingSymbols: number): ISymbolData[] =>
    {
        this.remainingSymbols = remainingSymbols;

        this.resetValidSymbols();

        this.symbols = symbols;

        if (this.reelConfigs.length > 0)
        {
            this.validSymbols = this.reelConfigsFilter(symbols);
        }
        else
        {
            this.validSymbols = this.symbolMatchFilter(symbols);
        }

        // Child rules.
        this.validSymbols = this.applyRuleFilter();

        // Get out and avoid filter executions.
        if (this.validSymbols.length < 1) { return this.validSymbols; }

        // Filter executions.
        this.filters.forEach(x =>
            {
                this.validSymbols = x.Filter(this.validSymbols);
                if (this.validSymbols.length < 1) { return this.validSymbols; }
            });

        // Base rules.
        this.validSymbols = this.minSymbolCountFilter(this.validSymbols);
        if (this.maxSymbolCount > 0) { this.validSymbols = this.maxSymbolCountFilter(this.validSymbols); }

        // Can we STILL win?
        this.validSymbols = this.minSymbolsToWinFilter(this.validSymbols);

        // Killed by filters..
        if (this.validSymbols.length < 1) { return this.validSymbols; }

        return this.sendMessages();
    }

    /** @hidden */
    protected sendMessages(): ISymbolData[]
    {
        // Send message to nodes.
        if (this.validSymbols.length >= this.minSymbolCount)
        {
            this.validSymbols.forEach(symbol =>
            {
                if (this._isReelSpecific && this.streakReels.indexOf(symbol.ReelIndex) < 0) { return; }
                this.sendOnStreakEvent(symbol);
            }, this);
        }
        else
        {
            this.validSymbols = [];
        }

        return this.validSymbols;
    }

    /** Dispatch clear event to all dispatched before. */
    public ClearDispatched(): void
    {
        this.hasStreaked = false;
        if (this.dispatchedSymbolNodes.length < 1) { return; }

        this.dispatchedSymbolNodes.forEach(this.sendOnClearEvent.bind(this));
        this.dispatchedSymbolNodes = [];
    }

    /**
     * Dispatch [[StreakEvent]] to given reel [[Symbol]].
     * @param symbol [[Symbol]] to dispatch to.
     */
    protected sendOnStreakEvent = (symbol: ISymbolData): void =>
    {
        if (this.dispatchedSymbolNodes.indexOf(symbol.Node) < 0)
        {
            symbol.Node.dispatchEvent(new cc.Event.EventCustom(this.OnStreakEvent, true));
            this.dispatchedSymbolNodes.push(symbol.Node);
        }

        this.hasStreaked = true;
    }

    /**
     * Dispatch [[StreakEvent]] to clear reel [[Symbol]].
     * @param symbol [[Symbol]] to dispatch to.
     */
    protected sendOnClearEvent = (symbolNode: cc.Node): void =>
    {
        symbolNode.dispatchEvent(new cc.Event.EventCustom(this.OnClearEvent, true));
    }

    /** Abstract method for running rule. */
    protected abstract applyRuleFilter(): ISymbolData[];

    /**
     * Base filter for [[Symbol]] ID.
     * @param symbols [[Symbol]] information to filter on.
     * @returns valid symbols.
     */
    protected symbolMatchFilter(symbols: ISymbolData[]): ISymbolData[]
    {
        return symbols.filter(symbolData => symbolData.SymbolId === this.symbolId);
    }

    /**
     * Base filter for minimum symbol count.
     * @param symbols [[Symbol]] information to filter on.
     * @returns valid symbols.
     */
    protected minSymbolCountFilter(symbols: ISymbolData[]): ISymbolData[]
    {
        return this.getSymbolListWorth(symbols) >= this.minSymbolCount ? symbols : [];
    }

    /**
     * Base filter for maximum symbol count.
     * @param symbols [[Symbol]] information to filter on.
     * @returns valid symbols.
     */
    protected maxSymbolCountFilter(symbols: ISymbolData[]): ISymbolData[]
    {
        return symbols.length <= this.maxSymbolCount ? symbols : [];
    }

    /**
     * Base filter for child [[ReelConfigComponent]]s.
     * @param symbols [[Symbol]] information to filter on.
     * @returns valid symbols.
     */
    protected reelConfigsFilter(symbols: ISymbolData[]): ISymbolData[]
    {
        if (this.reelConfigs.length < 1) { return symbols; }

        let validSymbols: ISymbolData[] = [];

        for (let config of this.reelConfigs)
        {
            // Find which belong to this reel.
            let matchedSymbols: ISymbolData[] = symbols.filter(x => x.ReelIndex === config.ReelIndex);

            // Add only those that match the config values.
            for (let symbol of matchedSymbols)
            {
                if (config.SymbolId && config.SymbolId.indexOf(symbol.SymbolId) < 0) { continue; }
                if (config.SymbolOffset !== ReelConfigComponent.AllOffsets
                    &&  config.SymbolOffset !== symbol.OffSet) { continue; }

                if (validSymbols.indexOf(symbol) < 0) { validSymbols.push(symbol); }
            }
        }

        return validSymbols;
    }

    /**
     * Base filter for minimum symbosl to win.
     * @param symbols [[Symbol]] information to filter on.
     * @returns valid symbols.
     */
    protected minSymbolsToWinFilter(symbols: ISymbolData[]): ISymbolData[]
    {
        // With remaining, is win still possible?
        let worth: number = this.getSymbolListWorth(symbols) +
            (this.symbolWorthMap[this.symbolId] || SymbolWorthComponent.DefaultValue) * this.remainingSymbols;

        return (worth < this.minSymbolsToWin)
            ? []
            : symbols;
    }

    protected addActivePaylineFilter(): void
    {
        if (this.getComponentInChildren(ActivePaylineFilter)) { return; }

        if (this.node.childrenCount < 1)
        {
            this.node.addChild(new cc.Node('ActivePaylineFilter'));
        }

        this.node.children[0].addComponent(ActivePaylineFilter);
    }

    /**
     * Get summed value of symbol list based on symbol worths.
     * @param symbols [[Symbol]] information to filter on.
     * @returns Summation of occurences based on [[SymbolWorth]]s.
     */
    private getSymbolListWorth(symbols: ISymbolData[]): number
    {
        let total: number = 0;

        symbols.forEach(x => total += this.symbolWorthMap[x.SymbolId] || SymbolWorthComponent.DefaultValue, this);

        return total;
    }

    /** Empties valid symbols. */
    private resetValidSymbols(): void
    {
        this.validSymbols = [];
    }
}
